import { ConsultChargeDoctor } from '@/components/PatientPageV2/components/ConsultationTabSections/ConsultCharge/Doctor/ConsultChargeDoctor';
import { ConsultChargeNurse } from '@/components/PatientPageV2/components/ConsultationTabSections/ConsultCharge/Nurse/ConsultChargeNurse';
import { ConsultType, getConsultationType } from '@/components/PatientPageV2/utils/getConsultationType';
import { isDoctor, isManager, isNurse, isSuperAdmin } from '@/data/service/authService';
import { useChargeInitialDoctorConsult } from '@/hooks/consult/useChargeInitialDoctorConsult';
import { ConsultPaymentStateEnum as PaymentState } from '@/hooks/consult/useConsult';
import { usePaymentStatus } from '@/hooks/consult/usePaymentStatus';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import { useConsultationStore } from '@/state-management/slices/consultations/hooks/useConsultationStore';
import { Alert, AlertTitle, Typography } from '@montugroup/design-system';
import { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import type { ShowConsultation } from '../ShowConsultation';

interface ConsultChargeProps {
  variant: 'nurse' | 'doctor';
  consultation: Consultation | ShowConsultation;
  isConcessionPatient?: boolean;
}
type RoleChecks = Record<ConsultChargeProps['variant'], () => boolean>;

const ConsultCharge = ({ variant, consultation, isConcessionPatient = false }: ConsultChargeProps) => {
  const roleChecks: RoleChecks = {
    nurse: isNurse,
    doctor: isDoctor,
  };
  const isManagerOrSuperAdmin = isSuperAdmin() || isManager();
  const isAuthorised =
    (isManagerOrSuperAdmin && variant === 'nurse') || (roleChecks[variant] ? roleChecks[variant]() : false);

  const { paymentRequestType, isMarkedAsComplimentary, setConsultationField } = useConsultationStore(consultation);

  const { shouldNotChargeInitialDoctorConsultation } = useChargeInitialDoctorConsult(
    consultation?.consult_type,
    consultation?.doctor_id,
    consultation?.hasPaidInitialNurseConsult,
  );

  const consultAndClinicianType = useMemo(() => getConsultationType(consultation), [consultation]);

  const paymentStatus = usePaymentStatus({
    paymentStatus: consultation.payment_status,
    paidDate: consultation?.paid_date,
    invoiceCode: consultation?.xero_consultation_code,
  });

  if (!isAuthorised || consultAndClinicianType === ConsultType.InitialNurse || !consultation) {
    return null;
  }

  const handlePaymentRequestTypeChange = (type: PaymentRequestType | null) => {
    setConsultationField(consultation.id, 'paymentRequestType', type);
  };

  const handleNurseIsMarkedAsComplimentaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsultationField(consultation.id, 'isMarkedAsComplimentary', e.target.checked);
    if (e.target.checked) {
      setConsultationField(consultation.id, 'paymentRequestType', PaymentRequestType.COMPLIMENTARY);
    } else {
      setConsultationField(consultation.id, 'paymentRequestType', null);
    }
  };

  const handleDoctorIsMarkedAsComplimentaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsultationField(consultation.id, 'isMarkedAsComplimentary', e.target.checked);
    if (e.target.checked) {
      setConsultationField(consultation.id, 'paymentRequestType', PaymentRequestType.COMPLIMENTARY);
    } else {
      setConsultationField(
        consultation.id,
        'paymentRequestType',
        isConcessionPatient ? PaymentRequestType.CONCESSION : PaymentRequestType.STANDARD,
      );
    }
  };

  const paymentInProgressOrPaid = [PaymentState.InProgress, PaymentState.Paid].includes(paymentStatus as PaymentState);

  const paymentSuccessTitle = 'Payment Confirmed';
  const paymentSuccessMessage = 'The consultation has been successfully processed and paid.';

  const renderConsultPaymentStatus = () => {
    if (shouldNotChargeInitialDoctorConsultation) {
      return (
        <Alert severity="success" sx={{ marginY: 4 }}>
          <AlertTitle>{paymentSuccessTitle}</AlertTitle>
          {paymentSuccessMessage}
        </Alert>
      );
    }

    switch (paymentStatus) {
      case PaymentState.InProgress:
        return (
          <Alert severity="info" sx={{ marginY: 4 }}>
            <AlertTitle>Payment Link Sent</AlertTitle>
            {`The patient has been sent a payment link${variant === 'nurse' ? ' by the doctor' : ''} to pay for their consultation.`}
          </Alert>
        );
      case PaymentState.Paid:
        return (
          <Alert severity="success" sx={{ marginY: 4 }}>
            <AlertTitle>{paymentSuccessTitle}</AlertTitle>
            {paymentSuccessMessage}
          </Alert>
        );
      default:
        return null;
    }
  };

  const renderConsultChargeUI = () => {
    if (variant === 'doctor') {
      return (
        <ConsultChargeDoctor
          isConcessionPatient={isConcessionPatient}
          onSelectionChange={handlePaymentRequestTypeChange}
          selectedPaymentRequestType={paymentRequestType ?? null}
          isComplimentaryForDoctor={isMarkedAsComplimentary ?? false}
          handleIsComplimentaryForDoctorChange={handleDoctorIsMarkedAsComplimentaryChange}
          isInitiallyMarkedAsComplimentary={consultation?.isMarkedAsComplimentary ?? false}
        />
      );
    }
    if (variant === 'nurse') {
      return (
        <ConsultChargeNurse
          isMarkedAsComplimentary={isMarkedAsComplimentary ?? false}
          onChangeIsMarkedAsComplimentary={handleNurseIsMarkedAsComplimentaryChange}
        />
      );
    }
    return null;
  };

  return (
    <Box component="section">
      <SectionHeader
        headerText={
          <>
            {'Consultation Charge'}
            {variant === 'doctor' && !paymentInProgressOrPaid ? (
              <Typography display="inline" fontSize={16} color="warning.main">
                {'* (required)'}
              </Typography>
            ) : null}
          </>
        }
      />

      {paymentInProgressOrPaid || shouldNotChargeInitialDoctorConsultation
        ? renderConsultPaymentStatus()
        : renderConsultChargeUI()}
    </Box>
  );
};

export { ConsultCharge };
